import { useState } from "react";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Ticket, CheckCircle2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { api, formatApiErrorDetail } from "../lib/api";
import { HOLIDAY_FAIR, VENDOR_CATEGORIES, IMAGES } from "../lib/content";

export default function HolidayFair() {
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    category: VENDOR_CATEGORIES[0],
    description: "",
    notes: "",
    website: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (
      !form.business_name.trim() ||
      !form.contact_name.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
    ) {
      setErr("Please add your business name, contact name, and a valid email.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/vendor", form);
      setDone(true);
      toast.success("Mahalo! Your vendor sign-up was received.");
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <>
      <PageHero
        eyebrow={`One day only · ${HOLIDAY_FAIR.hours}`}
        lines={[
          { text: "Kamalama" },
          { text: "Holiday Fair", className: "font-serifg italic font-semibold" },
        ]}
        lead="A community celebration for the whole ʻohana — local vendors, ʻohana food, keiki activities, and holiday cheer on Kauaʻi."
      />

      <section className="wrap py-16 sm:py-20">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl" data-testid="fair-promo">
            <img
              src={IMAGES.holidayFairPromo}
              alt="Illustration of the Kamalama Holiday Fair — vendor tents, string lights, and ʻohana by the ocean"
              className="h-72 w-full object-cover sm:h-96"
              data-testid="fair-promo-image"
            />
            <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-center gap-x-6 gap-y-2 bg-gradient-to-t from-black/70 to-transparent px-6 py-5 font-mono text-sm text-white">
              <span>{HOLIDAY_FAIR.date}</span>
              <span>{HOLIDAY_FAIR.hours}</span>
              <span>Venue to be announced</span>
            </div>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="fair-details">
          {[
            [<Calendar key="i" className="h-6 w-6 text-terra" aria-hidden="true" />, "Date", HOLIDAY_FAIR.date],
            [<Clock key="i" className="h-6 w-6 text-terra" aria-hidden="true" />, "Hours", HOLIDAY_FAIR.hours],
            [<MapPin key="i" className="h-6 w-6 text-terra" aria-hidden="true" />, "Location", "To be announced"],
            [<Ticket key="i" className="h-6 w-6 text-terra" aria-hidden="true" />, "Admission", "To be announced"],
          ].map(([icon, label, value], i) => (
            <Reveal key={label} delay={i * 0.06} className="h-full">
              <div className="card-surface h-full !gap-2">
                {icon}
                <p className="eyebrow">{label}</p>
                <p className="font-display text-lg font-bold leading-snug">{value}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-8">
          <div className="callout">
            <b className="font-display">What to expect</b>
            <p className="text-muted-foreground">
              Local vendors and makers, ʻohana food, keiki activities, and
              holiday cheer. Kamalama Foundation is producing this fair, and
              net proceeds after disclosed event expenses support our
              charitable programs, including Hurricane Lowell relief and
              recovery. Lineup, venue, and tickets will be announced here.
            </p>
          </div>
        </Reveal>
      </section>

      <section
        className="border-t border-border bg-muted/40"
        id="vendors"
        data-testid="fair-vendor-section"
      >
        <div className="wrap grid gap-10 py-16 sm:py-20 lg:grid-cols-2">
          <Reveal className="flex flex-col items-start gap-4">
            <p className="eyebrow">Vendor sign-up</p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Vendors, join us
            </h2>
            <p className="leading-relaxed text-muted-foreground">
              Local businesses and makers come first. Vendor fees, booth
              sizes, and participation details are being finalized from our
              Fundraiser Meeting Minutes and will be posted here — sign up now
              and you'll get the vendor packet first.
            </p>
            <p className="text-sm text-muted-foreground">
              Questions? Email kamalamafoundation@gmail.com or call (808)
              631-8672.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            {done ? (
              <div
                className="card-surface border-primary/40"
                data-testid="vendor-success-card"
              >
                <CheckCircle2 className="h-10 w-10 text-primary dark:text-accent" />
                <h3 className="font-display text-2xl font-bold">Mahalo!</h3>
                <p className="text-muted-foreground">
                  Your vendor sign-up was received. We'll email the vendor
                  packet with booth details and fees as soon as they're
                  confirmed.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="flex flex-col gap-4" data-testid="vendor-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label className="label" htmlFor="v-biz">Business name</label>
                    <input id="v-biz" className="field" type="text" required value={form.business_name} onChange={set("business_name")} data-testid="vendor-business-input" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="label" htmlFor="v-contact">Contact name</label>
                    <input id="v-contact" className="field" type="text" autoComplete="name" required value={form.contact_name} onChange={set("contact_name")} data-testid="vendor-contact-input" />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label className="label" htmlFor="v-email">Email</label>
                    <input id="v-email" className="field" type="email" autoComplete="email" required value={form.email} onChange={set("email")} data-testid="vendor-email-input" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="label" htmlFor="v-phone">Phone</label>
                    <input id="v-phone" className="field" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} data-testid="vendor-phone-input" />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="label" htmlFor="v-cat">What do you sell or share?</label>
                  <select id="v-cat" className="field" value={form.category} onChange={set("category")} data-testid="vendor-category-select">
                    {VENDOR_CATEGORIES.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="label" htmlFor="v-desc">Tell us about your booth</label>
                  <textarea id="v-desc" className="field min-h-[110px]" value={form.description} onChange={set("description")} data-testid="vendor-description-input" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="label" htmlFor="v-notes">Anything we should know? (optional)</label>
                  <textarea id="v-notes" className="field min-h-[70px]" value={form.notes} onChange={set("notes")} data-testid="vendor-notes-input" />
                </div>
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={form.website}
                  onChange={set("website")}
                  className="pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0"
                />
                {err && (
                  <p className="text-sm font-semibold text-destructive" role="alert" data-testid="vendor-form-error">
                    {err}
                  </p>
                )}
                <button className="btn w-fit" type="submit" disabled={busy} data-testid="vendor-form-submit">
                  {busy ? "Signing up…" : "Sign up as a vendor"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
